import Avatar from '@components/avatar';
import { CheckmarkOutlined, CloseOutlined, PersonFilledOutlined } from '@icon';
import { MenuItemType, StatusBadge, Table, TableProps } from '@ui';
import React from 'react';
import { useHistory } from 'react-router-dom';
import type { ColumnWithLooseAccessor } from 'react-table';
import { IClientSearchItem, IClientSearchParams, TClientPairingStatus } from 'types/client.types';
import { Full } from 'types/common';
import ClientStatusTag from './components/client-status-tag';

export type TTableProps = Full<TableProps<IClientSearchItem>>;

export const INITIAL_TABLE_STATE: TTableProps['initialState'] = {
  sortBy: [{ id: 'createdAt', desc: true }],
  pageIndex: 0,
  pageSize: 15,
};

export const INITIAL_SEARCH_PARAMS: IClientSearchParams = {
  sortBy: 'createdAt',
  direction: 'DESC',
  page: 1,
  pageSize: 15,
  myClients: null,
};

const emptyActionsStatuses: TClientPairingStatus[] = ['invited', 'rejected'];

type TColumn = ColumnWithLooseAccessor<IClientSearchItem>;

type TUseColumns = {
  canEdit?: boolean;
  onPairClick: (
    e: React.MouseEvent<Element, MouseEvent>,
    client: IClientSearchItem,
    status: TClientPairingStatus,
  ) => void;
};

export function useColumns(params: TUseColumns) {
  const { onPairClick, canEdit } = params;
  const { push } = useHistory();

  return React.useMemo<TColumn[]>(
    () => [
      {
        Header: '',
        id: 'avatar',
        Cell: ({ row: { original } }) => <Avatar imgUrl={original.profile.profileImage} />,
        className: 'asd',
      },
      {
        Header: 'Meno',
        id: 'firstName',
        accessor: 'firstName' as any,
        disableSortBy: false,
        Cell: ({ row: { original } }) => (
          <span style={{ whiteSpace: 'nowrap' }}>
            {`${original.profile.firstName} ${original.profile.lastName}`}
            {original.myClient ? (
              <StatusBadge variant="info" size="small" style={{ marginLeft: 8 }} />
            ) : null}
          </span>
        ),
      },
      {
        Header: 'Rodné číslo',
        id: 'pin',
        Cell: ({ row: { original } }) => <>{`${original.profile.pin ?? '-'}`}</>,
      },
      {
        Header: 'Tel. č.',
        id: 'email',
        Cell: ({ row: { original } }) => <>{`${original.phoneNumber ?? '-'}`}</>,
      },
      {
        Header: 'Stav',
        id: 'status',
        Cell: ({ row: { original } }) => <ClientStatusTag status={original.pairing.status} />,
      },
      {
        Header: '',
        id: 'actions',
        Cell: ({ row: { original } }) => {
          let items: MenuItemType[] = [];

          if (!canEdit || emptyActionsStatuses.includes(original.pairing.status)) {
            return <></>;
          }

          if (original.pairing.status === 'pending') {
            items = [
              {
                label: 'Prijat',
                key: 'accept',
                Icon: CheckmarkOutlined,
                variant: 'success',
                onClick: (e) => {
                  onPairClick(e.domEvent as React.MouseEvent<HTMLElement>, original, 'paired');
                },
              },
              { type: 'divider' },
              {
                label: 'Odmietnut',
                key: 'delete',
                variant: 'danger',
                Icon: CloseOutlined,
                onClick: (e) => {
                  onPairClick(e.domEvent as React.MouseEvent<HTMLElement>, original, 'rejected');
                },
              },
            ];
          } else if (original.pairing.status === 'paired') {
            items = [
              {
                key: 'detail',
                label: 'Zobraziť detail',
                Icon: PersonFilledOutlined,
                onClick: () => {
                  push(`/life-id/${original.id}`);
                },
              },
            ];
          }

          return <Table.Actions items={items} />;
        },
      },
    ],
    [onPairClick, canEdit],
  );
}
